import { faGoogle } from "@fortawesome/free-brands-svg-icons/faGoogle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Anchor,
  Button,
  Divider,
  Group,
  Paper,
  PaperProps,
  PasswordInput,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { upperFirst, useToggle } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import {
  AuthError,
  browserPopupRedirectResolver,
  createUserWithEmailAndPassword,
  getAuth,
  GoogleAuthProvider,
  signInWithCredential,
  signInWithEmailAndPassword,
  signInWithPopup,
  User,
} from "firebase/auth";
import { useEffect, useState } from "react";
import { loadGoogleIdentityServices } from "../../utils/googleIdentityServices";
import { createLogger } from "../../utils/logger";
import { openPoliciesModal } from "../PoliciesModal";
import { checkAndPromptDisplayName } from "./useDisplayNamePrompt";
import { LOGIN_MODAL_ID } from "./useOpenLoginModal";

const logger = createLogger("LoginForm");

interface LoginFormProps extends PaperProps {
  onLoginSuccess?: () => void;
}

const LoginForm = (props: LoginFormProps) => {
  const { onLoginSuccess, ...paperProps } = props;
  const [loginError, setLoginError] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [type, toggle] = useToggle(["login", "register"]);
  const form = useForm({
    initialValues: {
      email: "",
      name: "",
      password: "",
      terms: true,
    },

    validate: {
      email: (val) => (/^\S+@\S+$/.test(val) ? null : "Invalid email"),
      password: (val) =>
        val.length <= 6
          ? "Password should include at least 6 characters"
          : null,
    },
  });

  const onAuthSuccess = async (user: User, isNewUser: boolean = false) => {
    notifications.show({
      title: "Welcome",
      message: "You're now signed in",
      color: "green",
    });
    modals.close(LOGIN_MODAL_ID);

    // For new users, always prompt for display name
    // For existing users, check if they have one
    if (isNewUser) {
      // Get the ID token and prompt for display name
      const token = await user.getIdToken();
      checkAndPromptDisplayName(token);
    } else {
      // Check if existing user has a display name
      try {
        const token = await user.getIdToken();
        checkAndPromptDisplayName(token);
      } catch (error) {
        logger.error("Error checking display name:", error);
      }
    }

    // Execute the callback if provided
    if (onLoginSuccess) {
      onLoginSuccess();
    }
  };

  const onAuthFailure = (error: AuthError) => {
    notifications.show({
      title: "Authentication error",
      message: error.message,
      color: "red",
    });
  };

  const auth = getAuth();

  useEffect(() => {
    let cancelled = false;

    // Await the script rather than assuming it has arrived. The old code read
    // the bare `google` global in this effect, which threw ReferenceError if
    // the async tag had not run yet - optional chaining does not guard an
    // undeclared identifier.
    loadGoogleIdentityServices()
      .then((google) => {
        if (cancelled) return;

        google.accounts.id.initialize({
          client_id:
            "268135617954-nvu6tk6rnvchiet9ku2lgaqhnrevfppo.apps.googleusercontent.com",
          use_fedcm_for_prompt: true,
          callback: (response: { credential: string }) => {
            const cred = GoogleAuthProvider.credential(response.credential);
            signInWithCredential(auth, cred)
              .then((userCredential) => {
                const isNewUser =
                  userCredential.user.metadata.creationTime ===
                  userCredential.user.metadata.lastSignInTime;
                onAuthSuccess(userCredential.user, isNewUser);
              })
              .catch(onAuthFailure)
              .finally(() => setIsLoading(false));
          },
        });

        google.accounts.id.prompt();
      })
      .catch((error) => {
        // One Tap is a convenience: the email/password and popup paths in this
        // same form still work, so a blocked or failed load must not surface.
        logger.error("Google One Tap unavailable", error);
      });

    return () => {
      cancelled = true;
    };
  }, [auth]);

  return (
    <Paper radius="md" {...paperProps}>
      <Group grow mb="md">
        <Button
          leftSection={<FontAwesomeIcon icon={faGoogle} />}
          onClick={() => {
            setIsLoading(true);
            // The resolver is passed here rather than at auth init so its
            // iframe loads when someone actually signs in, not on every
            // anonymous page load. See utils/firebaseAuth.ts.
            signInWithPopup(
              auth,
              new GoogleAuthProvider(),
              browserPopupRedirectResolver
            )
              .then((userCredential) => {
                const isNewUser =
                  userCredential.user.metadata.creationTime ===
                  userCredential.user.metadata.lastSignInTime;
                onAuthSuccess(userCredential.user, isNewUser);
              })
              .catch(onAuthFailure)
              .finally(() => setIsLoading(false));
          }}
          disabled={isLoading}
        >
          Google
        </Button>
        {/* TODO: Facebook/Apple/Passkey */}
      </Group>

      <Divider label="Or continue with email" labelPosition="center" my="lg" />

      <form
        onSubmit={form.onSubmit(() => {
          const fnToCall =
            type === "login"
              ? signInWithEmailAndPassword
              : createUserWithEmailAndPassword;
          const isRegistering = type === "register";
          setIsLoading(true);
          fnToCall(auth, form.values.email, form.values.password)
            .then((userCredential) => {
              // For registration, it's always a new user
              // For login, check if it's their first sign-in
              const isNewUser =
                isRegistering ||
                userCredential.user.metadata.creationTime ===
                  userCredential.user.metadata.lastSignInTime;
              onAuthSuccess(userCredential.user, isNewUser);
            })
            .catch((error: AuthError) => {
              setLoginError(error.message);
            })
            .finally(() => setIsLoading(false));
        })}
      >
        <Stack>
          <TextInput
            required
            label="Email"
            placeholder="hello@iwanna.camp"
            value={form.values.email}
            onChange={(event) =>
              form.setFieldValue("email", event.currentTarget.value)
            }
            error={form.errors.email && "Invalid email"}
            radius="md"
            disabled={isLoading}
          />

          <PasswordInput
            required
            label="Password"
            placeholder="Your password"
            value={form.values.password}
            onChange={(event) =>
              form.setFieldValue("password", event.currentTarget.value)
            }
            error={
              form.errors.password &&
              "Password should include at least 6 characters"
            }
            radius="md"
            disabled={isLoading}
          />
        </Stack>

        <Group justify="space-between" mt="xl" wrap="nowrap">
          <Stack gap="0.3rem" style={{ alignItems: "flex-start" }} flex={2}>
            <Anchor
              component="button"
              type="button"
              c="dimmed"
              onClick={() => toggle()}
              size="xs"
            >
              {type === "register"
                ? "Already have an account? Login"
                : "Don't have an account? Register"}
            </Anchor>

            <Anchor
              size="xs"
              c="dimmed"
              flex={2}
              onClick={() => openPoliciesModal()}
            >
              By continuing, you agree to I Wanna Camp's{" "}
              <Text
                style={{
                  textDecorationStyle: "dotted",
                  textDecorationLine: "underline",
                }}
                span
              >
                policies
              </Text>
              .
            </Anchor>
          </Stack>
          <Button type="submit" radius="xl" disabled={isLoading} flex={1}>
            {upperFirst(type)}
          </Button>
        </Group>

        {loginError && (
          <div style={{ color: "red", textAlign: "center" }}>{loginError}</div>
        )}
      </form>
    </Paper>
  );
};

export default LoginForm;
